import type { HomeWidgetId } from "@/lib/ui/home-widgets";

export const HOME_WIDGET_CONFIG_COOKIE = "pinwiki_home_widget_config";

export const CONFIGURABLE_HOME_WIDGETS = [
  "recent",
  "indexing",
  "spend",
  "quota",
  "spendusers",
] as const;

export type ConfigurableHomeWidgetId =
  (typeof CONFIGURABLE_HOME_WIDGETS)[number];

export type HomeWidgetWidth = 1 | 2 | 3;

export const RECENT_OPTIONAL_COLUMNS = [
  "type",
  "collection",
  "owner",
  "department",
  "updated",
  "version",
] as const;

export type RecentOptionalColumn = (typeof RECENT_OPTIONAL_COLUMNS)[number];

export const INDEXING_OPTIONAL_COLUMNS = ["collection", "owner", "version"] as const;

export type IndexingOptionalColumn = (typeof INDEXING_OPTIONAL_COLUMNS)[number];

export const SPEND_USERS_OPTIONAL_COLUMNS = ["department"] as const;

export type SpendUsersOptionalColumn =
  (typeof SPEND_USERS_OPTIONAL_COLUMNS)[number];

export const SPEND_OPTIONAL_BLOCKS = [
  "balance",
  "spent",
  "chart",
  "model",
] as const;

export type SpendOptionalBlock = (typeof SPEND_OPTIONAL_BLOCKS)[number];

export const QUOTA_OPTIONAL_BLOCKS = [
  "remaining",
  "limit",
  "spent",
  "chart",
] as const;

export type QuotaOptionalBlock = (typeof QUOTA_OPTIONAL_BLOCKS)[number];

export type HomeWidgetConfigEntry = {
  units: HomeWidgetWidth;
  columns: string[];
};

export type HomeWidgetConfigMap = Partial<
  Record<ConfigurableHomeWidgetId, HomeWidgetConfigEntry>
>;

const DEFAULT_UNITS: Record<ConfigurableHomeWidgetId, HomeWidgetWidth> = {
  recent: 2,
  indexing: 2,
  spend: 1,
  quota: 1,
  spendusers: 2,
};

const OPTIONAL_COLUMNS: Record<ConfigurableHomeWidgetId, readonly string[]> = {
  recent: RECENT_OPTIONAL_COLUMNS,
  indexing: INDEXING_OPTIONAL_COLUMNS,
  spend: SPEND_OPTIONAL_BLOCKS,
  quota: QUOTA_OPTIONAL_BLOCKS,
  spendusers: SPEND_USERS_OPTIONAL_COLUMNS,
};

/** Минимальная ширина — только для дефолтов при первом добавлении виджета. */
const DEFAULT_COLUMN_MIN_WIDTH: Record<
  ConfigurableHomeWidgetId,
  Record<string, HomeWidgetWidth>
> = {
  recent: {
    type: 1,
    collection: 2,
    owner: 2,
    department: 3,
    updated: 3,
    version: 1,
  },
  indexing: {
    collection: 3,
    owner: 3,
    version: 3,
  },
  spend: {
    balance: 1,
    spent: 1,
    chart: 1,
    model: 1,
  },
  quota: {
    remaining: 1,
    limit: 1,
    spent: 1,
    chart: 1,
  },
  spendusers: {
    department: 2,
  },
};

export function optionalColumnsForWidget(
  id: ConfigurableHomeWidgetId,
): readonly string[] {
  return OPTIONAL_COLUMNS[id];
}

export function isConfigurableHomeWidget(
  value: string,
): value is ConfigurableHomeWidgetId {
  return (CONFIGURABLE_HOME_WIDGETS as readonly string[]).includes(value);
}

export function maxWidthForWidget(
  id: ConfigurableHomeWidgetId,
): HomeWidgetWidth {
  return id === "spend" || id === "quota" ? 2 : 3;
}

export function widthsForWidget(
  id: ConfigurableHomeWidgetId,
): readonly HomeWidgetWidth[] {
  return id === "spend" || id === "quota" ? [1, 2] : [1, 2, 3];
}

export function clampHomeWidgetWidth(
  value: number,
  max: HomeWidgetWidth = 3,
): HomeWidgetWidth {
  if (value <= 1) {
    return 1;
  }
  if (value >= 3) {
    return max;
  }
  if (value >= max) {
    return max;
  }
  return 2;
}

export function defaultColumnsForWidth(
  id: ConfigurableHomeWidgetId,
  units: HomeWidgetWidth,
): string[] {
  const min = DEFAULT_COLUMN_MIN_WIDTH[id];
  return Object.entries(min)
    .filter(([, width]) => width <= units)
    .map(([column]) => column);
}

export function clampHomeWidgetConfigEntry(
  id: ConfigurableHomeWidgetId,
  entry: HomeWidgetConfigEntry,
): HomeWidgetConfigEntry {
  const units = clampHomeWidgetWidth(entry.units, maxWidthForWidget(id));
  const allowed = new Set(optionalColumnsForWidget(id));
  const columns = entry.columns.filter((column) => allowed.has(column));
  return { units, columns };
}

export function resolveHomeWidgetConfigEntry(
  id: ConfigurableHomeWidgetId,
  config: HomeWidgetConfigMap = {},
): HomeWidgetConfigEntry {
  const stored = config[id];
  if (!stored) {
    const units = DEFAULT_UNITS[id];
    return {
      units,
      columns: defaultColumnsForWidth(id, units),
    };
  }
  return clampHomeWidgetConfigEntry(id, stored);
}

const FIXED_WIDGET_UNITS: Partial<Record<HomeWidgetId, number>> = {
  server: 1,
  schedule: 1,
};

export function homeWidgetUnitsFromConfig(
  id: HomeWidgetId,
  config: HomeWidgetConfigMap = {},
): number {
  if (isConfigurableHomeWidget(id)) {
    return resolveHomeWidgetConfigEntry(id, config).units;
  }
  return FIXED_WIDGET_UNITS[id] ?? 1;
}

export function homeWidgetSpanFromConfig(
  id: HomeWidgetId,
  config: HomeWidgetConfigMap = {},
): number {
  return homeWidgetUnitsFromConfig(id, config) * 4;
}

export function parseHomeWidgetConfig(
  raw: string | null | undefined,
): HomeWidgetConfigMap {
  if (raw == null) {
    return {};
  }
  const value = raw.trim();
  if (value.length === 0) {
    return {};
  }
  const config: HomeWidgetConfigMap = {};
  for (const part of value.split(";")) {
    const trimmed = part.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const [idRaw, unitsRaw, columnsRaw = ""] = trimmed.split(":");
    if (!isConfigurableHomeWidget(idRaw?.trim() ?? "")) {
      continue;
    }
    const id = idRaw.trim() as ConfigurableHomeWidgetId;
    const units = clampHomeWidgetWidth(Number(unitsRaw));
    const columns = columnsRaw
      .split(",")
      .map((column) => column.trim())
      .filter((column) => column.length > 0);
    config[id] = clampHomeWidgetConfigEntry(id, { units, columns });
  }
  return config;
}

export function serializeHomeWidgetConfig(config: HomeWidgetConfigMap): string {
  return CONFIGURABLE_HOME_WIDGETS.flatMap((id) => {
    const entry = config[id];
    if (!entry) {
      return [];
    }
    const clamped = clampHomeWidgetConfigEntry(id, entry);
    return [`${id}:${clamped.units}:${clamped.columns.join(",")}`];
  }).join(";");
}

export function setHomeWidgetConfigEntry(
  config: HomeWidgetConfigMap,
  id: ConfigurableHomeWidgetId,
  entry: HomeWidgetConfigEntry,
): HomeWidgetConfigMap {
  return {
    ...config,
    [id]: clampHomeWidgetConfigEntry(id, entry),
  };
}

export function recentColumnsVisible(
  entry: HomeWidgetConfigEntry,
): Record<RecentOptionalColumn, boolean> {
  const set = new Set(entry.columns);
  return {
    type: set.has("type"),
    collection: set.has("collection"),
    owner: set.has("owner"),
    department: set.has("department"),
    updated: set.has("updated"),
    version: set.has("version"),
  };
}

export function indexingColumnsVisible(
  entry: HomeWidgetConfigEntry,
): Record<IndexingOptionalColumn, boolean> {
  const set = new Set(entry.columns);
  return {
    collection: set.has("collection"),
    owner: set.has("owner"),
    version: set.has("version"),
  };
}

export function spendUsersColumnsVisible(
  entry: HomeWidgetConfigEntry,
): Record<SpendUsersOptionalColumn, boolean> {
  return {
    department: entry.columns.includes("department"),
  };
}

export function spendBlocksVisible(
  entry: HomeWidgetConfigEntry,
): Record<SpendOptionalBlock, boolean> {
  const set = new Set(entry.columns);
  return {
    balance: set.has("balance"),
    spent: set.has("spent"),
    chart: set.has("chart"),
    model: set.has("model"),
  };
}

export function quotaBlocksVisible(
  entry: HomeWidgetConfigEntry,
): Record<QuotaOptionalBlock, boolean> {
  const set = new Set(entry.columns);
  return {
    remaining: set.has("remaining"),
    limit: set.has("limit"),
    spent: set.has("spent"),
    chart: set.has("chart"),
  };
}
